import { z } from 'zod/v4';

import {
    animationPluginShapeLegacy,
    buttonHorizontalPositionSchema,
    extendedPositionPluginShapeLegacy,
    visibilityPluginShapeLegacy,
    widgetPositionPluginShapeLegacy,
} from '../../../common/schemas/legacy';

/**
 * Field shape shared by both placement variants of the text widget — mirrors
 * the FormGroup produced by `getDefaultTextWidgetData`/`getZeroTextWidgetData`
 * in the host app's text form factory. The variants diverge **only on
 * `position`** (simple per-device grid vs extended-position grid), so — like
 * the subscription-form widget — the data schemas share a common shape and
 * only override `position`.
 *
 * `content` is the rich-text HTML string. `horizontalAlign` reuses the shared
 * `buttonHorizontalPositionSchema` (`'L' | 'M' | 'R'`, mirroring
 * `EHorizontalPosition`). `isSynchronizeStyle` is `nullable()`: very old
 * widgets persisted `null`, which the editor treats as "off" (truthy check).
 */
const textCommonDataShape = {
    content: z.string(),
    horizontalAlign: buttonHorizontalPositionSchema,
    isSynchronizeStyle: z.boolean().nullable(),
    ...visibilityPluginShapeLegacy,
    ...animationPluginShapeLegacy,
} as const;

/**
 * Data shape for a **default-placement** text widget — common fields plus the
 * simple per-device position grid (`nullish()`: widgets persisted before the
 * position group shipped omit it).
 */
export const textLegacyDefaultDataSchema = z.object({
    ...textCommonDataShape,
    ...widgetPositionPluginShapeLegacy,
});

/**
 * Data shape for a **zero-block-placement** text widget — common fields plus
 * the extended-position grid (per-device width/height/x/y/rotate/zIndex). Zero
 * blocks post-date the widget, so the extended position is required.
 */
export const textLegacyZeroDataSchema = z.object({
    ...textCommonDataShape,
    ...extendedPositionPluginShapeLegacy,
});
